import { Profile } from '@app/routes/content/routes/recordings/components/icons';
import { TranscriptSegmentGroup } from '@app/routes/content/routes/recordings/domain';
import { selectRecordingTranscriptSpeakers } from '@app/routes/content/routes/recordings/selector';
import {
  calculateTextContainerHeight,
  getSpeakerDisplayName,
} from '@app/routes/content/routes/recordings/utils';
import { formatDuration } from '@app/routes/content/utils';
import { getContentHubMediaHost } from '@app/utilities/utils';
import cx from 'classnames';
import React from 'react';
import { useSelector } from 'react-redux';

import type { FlatWord } from './utils/build-flat-words';

type EditSegmentGroupProps = {
  group: TranscriptSegmentGroup;
  groupIndex: number;
  /** All flat words for this segment group (pre-sliced by the parent). */
  groupFlatWords: FlatWord[];
  /**
   * Flat-index offset: the first word of this group in the global flatWords
   * array. Used to map groupFlatWords[i] → global flat index for selection.
   */
  flatIndexOffset: number;
  /**
   * Set of global flat indices currently highlighted — covers both normal
   * word-selection and cut-gap selection. Both use the same highlight style.
   */
  highlightedIndices: Set<number>;
  /** Current playback time (seconds) — used to highlight the active word. */
  currentTimeSeconds: number;
  onWordMouseDown: (flatIdx: number) => void;
  onWordMouseEnter: (flatIdx: number) => void;
};

/**
 * Renders a single speaker group in the EditTranscript component.
 *
 * Each word is a `<span>` with interactive styling:
 *   - isCut         → strikethrough, muted, cursor-pointer (can click to restore gap)
 *   - correction    → dashed underline (shows corrected text)
 *   - isHighlighted → blue tint background (normal selection OR gap selection)
 *   - active word   → cobranding accent colour
 *   - normal        → default text colour
 */
export const EditSegmentGroup = ({
  group,
  groupIndex,
  groupFlatWords,
  flatIndexOffset,
  highlightedIndices,
  currentTimeSeconds,
  onWordMouseDown,
  onWordMouseEnter,
}: EditSegmentGroupProps) => {
  const speakersMapping = useSelector(selectRecordingTranscriptSpeakers);
  const { speaker, segments } = group;

  const displaySpeakerName = getSpeakerDisplayName(speaker, speakersMapping);
  const speakerData = speakersMapping?.[speaker];
  const speakerImageUrl = speakerData?.imageUrl ?? null;

  // Silence unused variable warning — segments is used only for typing context.
  void segments;

  return (
    <div>
      <div
        className="flex items-start gap-2 p-2 rounded-lg"
        data-group-index={groupIndex}
        data-testid="edit-transcript-segment-group"
      >
        {/* Speaker avatar */}
        <div className="w-8 h-8 rounded-full bg-spring-neutral-b4 flex items-center justify-center text-white shrink-0 relative">
          {speakerImageUrl ? (
            <div
              className="absolute inset-0 bg-center bg-cover bg-no-repeat rounded-full"
              style={{
                backgroundImage: `url('${
                  speakerImageUrl.startsWith('https')
                    ? speakerImageUrl
                    : `${getContentHubMediaHost()}${speakerImageUrl}`
                }')`,
              }}
            />
          ) : (
            <Profile className="w-5 h-5" />
          )}
        </div>

        {/* Content */}
        <div className="flex flex-col gap-1.5 flex-1 pt-0.5">
          {/* Speaker name + timestamp */}
          <div className="flex items-center justify-start gap-1">
            <span className="text-sm font-medium text-spring-neutral-b0">
              {displaySpeakerName || ''}
            </span>
            <div className="w-0.5 h-0.5 rounded-full bg-spring-neutral-b1/35 shrink-0" />
            <span className="text-xs text-spring-neutral-b1/35 shrink-0">
              {formatDuration(group.start)}
            </span>
          </div>

          {/* Word spans */}
          <div
            className="text-sm font-normal leading-6 select-none"
            data-testid="edit-transcript-words"
          >
            {groupFlatWords.map((fw, localIdx) => {
              const globalIdx = flatIndexOffset + localIdx;
              const isHighlighted = highlightedIndices.has(globalIdx);

              const isActiveWord =
                currentTimeSeconds >= fw.word.start &&
                currentTimeSeconds < fw.word.end;

              const displayText = fw.correction ?? fw.word.word;

              return (
                <React.Fragment key={`w-${fw.segmentIndex}-${fw.wordIndex}`}>
                  <span
                    role="button"
                    tabIndex={0}
                    aria-label={displayText}
                    aria-pressed={isHighlighted}
                    className={cx(
                      'transition-colors duration-100 rounded-sm pl-0.5 focus:outline-none focus-visible:ring-2 focus-visible:ring-spring-primary',
                      {
                        // Cut: struck through and muted — clickable to restore the gap
                        'line-through text-spring-neutral-b1/50 cursor-pointer':
                          fw.isCut,
                        // Correction: dashed underline
                        'border-b-2 border-dashed border-blue-400':
                          !fw.isCut && fw.correction !== undefined,
                        // Highlighted (selection OR gap) — same colour for both
                        'bg-spring-primary rounded': isHighlighted,
                        // Active playback word (only when not highlighted)
                        'text-spring-cobranding':
                          isActiveWord && !fw.isCut && !isHighlighted,
                        // Normal text
                        'text-spring-neutral-b0 cursor-pointer':
                          !fw.isCut && !isActiveWord,
                      },
                    )}
                    onMouseDown={e => {
                      e.preventDefault(); // prevent browser text selection
                      onWordMouseDown(globalIdx);
                    }}
                    onMouseEnter={() => onWordMouseEnter(globalIdx)}
                    onKeyDown={e => {
                      if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        onWordMouseDown(globalIdx);
                      }
                    }}
                    data-flat-idx={globalIdx}
                    data-testid="edit-transcript-word"
                  >
                    {displayText}
                  </span>
                  {localIdx < groupFlatWords.length - 1 && ' '}
                </React.Fragment>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
};

// ── Size estimation (mirrors the existing getSegmentGroupSize) ──────────────

const CONTAINER_PADDING = 16;
const CONTAINER_GAP = 8;
const PROFILE_ICON_WIDTH = 32;
const SPEAKER_NAME_HEIGHT = 20;
const GAP_BETWEEN_SEGMENTS = 6;
const MEASUREMENT_OPTIONS = {
  fontSize: 14,
  fontFamily: 'Inter',
  fontWeight: 'normal',
  lineHeight: 16,
};

export const getEditSegmentGroupSize = (
  group: TranscriptSegmentGroup,
  containerWidth: number,
): number => {
  const { segments } = group;
  let currentHeight = CONTAINER_PADDING + SPEAKER_NAME_HEIGHT;

  const segmentContainerWidth =
    containerWidth - CONTAINER_PADDING * 4 - CONTAINER_GAP - PROFILE_ICON_WIDTH;

  segments.forEach(segment => {
    const textHeight = calculateTextContainerHeight(
      segment.text,
      segmentContainerWidth,
      MEASUREMENT_OPTIONS,
    );
    currentHeight += GAP_BETWEEN_SEGMENTS + textHeight;
  });

  return currentHeight;
};
